import { FilterInput, SortOrder } from '@kinetic-ui/data-layer';
import { TableProps as MuiTableProps } from '@mui/material';
import { FormOwnProps, FormValues } from '@qfa/forms';
import { DrawerControls, DrawerState } from 'kineticUtils/hooks';
import { MutableRefObject, PropsWithoutRef, ReactNode, RefAttributes } from 'react';
import { VariableSizeList } from 'react-window';
import { PREFERENCE_KEYS } from 'types/preferenceTypes';
import { SetState } from 'types/props';
import { FilterUserPreferences } from './TableFiltersDrawerHeader';
import { TableRowOptions, TableRowsProps } from './TableRows/types';
import { ColumnConfig, UpdateConfig } from './contexts';

export type BaseRow = Record<'id', string>;

export interface TableProps<TRow extends BaseRow> extends MuiTableProps {
  data?: TRow[];
  selection?: string[];
  setSelection?: SetState<string[]>;
  onSortChange?: (sort: Record<string, SortOrder>) => void;
  extraContent?: ReactNode;
  /** if true - render LoadingIndicator */
  loading?: boolean;
  rowOptions?: Partial<TableRowOptions<TRow>>;
  renderView?: (props: TableRowsProps) => JSX.Element;
  listRef?: MutableRefObject<VariableSizeList>;
  hideColumnHeader?: boolean;
  sort?: SortParams;
}
export type TableRefProps = {
  download: (fileName: string, additionalData?: string[]) => void;
  getColumns: () => ColumnConfig<BaseRow>[];
};
export interface SortParams {
  order: SortOrder;
  field: string;
}

export interface TableFilter<T> {
  activeFilters: T;
  setActiveFilters: (values: Partial<T>) => void;
  searchText: string | null;
  setSearchText: (searchText: string) => void;
  areFiltersCleared: boolean;
  setAreFiltersCleared: (cleared: boolean) => void;
}

export interface TableFilters {
  [key: string]: TableFilter<any>;
}

export const TABLE_FILTER_PARAMS = 'tableFilters';

export interface TableFilterInput<FormType extends FormValues> {
  activeFilters: FilterInput[];
  setActiveFilters: (values: FilterInput[]) => void;
  searchText: string;
  setSearchText: (searchText: string) => void;
  setSelectedFilterPreference?: React.Dispatch<React.SetStateAction<string>>;
  selectedFilterPreference?: string;
  showFilterChips?: boolean;
  setShowFilterChips?: React.Dispatch<React.SetStateAction<boolean>>;
  columnFiltersOpen?: boolean;
  filterPreferenceKey?: PREFERENCE_KEYS;
  columnPreferenceKey?: PREFERENCE_KEYS;
  filterDrawerState?: DrawerState;
  filterDrawerControls?: DrawerControls;
  columnDrawerState?: DrawerState;
  columnDrawerControls?: DrawerControls;
  saveFilterPreference: (value: FilterUserPreferences) => Promise<any>;
  filterUserPreferences?: FilterUserPreferences;
  columnControlsPreference: UpdateConfig[];
  columnPreferenceChange: (columns: any) => void;
  formProps: FormOwnProps<FormType>;
  reset: (newValues?: Partial<FormType>) => void;
  isSaveDisabled?: boolean;
  hasErrors?: boolean;
}

/** @deprecated prefer CombinedTableControls */
export type TableFilterInputs = Record<string, TableFilterInput<any>>;

export type GenericProps<TRef, P> = PropsWithoutRef<P> & RefAttributes<TRef>;

//Infers the TRow type from the `data` prop
export type GenericTable = <TRow extends BaseRow>(
  props: GenericProps<TableRefProps, TableProps<TRow>>
) => JSX.Element;

export const defaultContextFilterValue = {
  saveFilterPreference: () => new Promise(() => {}),
  columnPreferenceChange: () => null,
  setActiveFilters: () => null,
  columnControlsPreference: [],
  setSearchText: () => null,
  activeFilters: [],
  reset: () => null,
  searchText: '',
  formProps: {
    onChange: () => null,
    onErrors: () => null,
    fields: [],
    values: {},
  },
};
